/**
 * GA4 ecommerce dataLayer events that can only be determined client side.
 *
 * Server-rendered events (view_item, view_item_list, view_cart, search) come
 * from snippets/datalayer.liquid and snippets/datalayer-view-item-list.liquid.
 *
 * Nothing here talks to GA4 directly. Every event is pushed to window.dataLayer
 * and GTM decides what to forward, so the destination measurement ID and the
 * consent gating both stay outside the theme.
 */
import { ThemeEvents } from '@theme/events';

/** Shared state seeded by snippets/datalayer.liquid on every page. */
const state = (window.arDataLayer = window.arDataLayer || {});
state.cart = state.cart || {};
state.lists = state.lists || {};

const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';

/**
 * Pushes an ecommerce event, deriving `value` from the items so it always
 * matches what is reported rather than the whole cart.
 * @param {string} event - GA4 event name
 * @param {Object[]} items - GA4 items array
 */
function pushEcommerce(event, items) {
  if (!items.length) return;

  const value = items.reduce((total, item) => total + item.price * item.quantity, 0);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ecommerce: {
      currency: state.currency || 'USD',
      value: Math.round(value * 100) / 100,
      items,
    },
  });
}

/**
 * Builds the reusable part of a GA4 item from a Shopify cart line. Cart lines
 * arrive in /cart.js shape, with prices in cents.
 * @param {Object} line - A Shopify cart line item
 */
function itemFromCartLine(line) {
  return {
    item_id: line.sku || String(line.variant_id),
    item_name: line.product_title || line.title,
    item_brand: line.vendor,
    item_category: line.product_type,
    item_variant: line.variant_title,
    price: (line.final_price ?? line.price ?? 0) / 100,
  };
}

/**
 * Reduces a cart to the shape snippets/datalayer.liquid seeds, so the two can
 * be diffed against each other.
 * @param {Object} cart - A Shopify cart object
 */
function snapshot(cart) {
  const next = {};

  for (const line of cart.items || []) {
    next[line.key] = { quantity: line.quantity, item: itemFromCartLine(line) };
  }

  return next;
}

/**
 * The theme dispatches the same 'cart:update' event for adds, quantity changes
 * and removals, so the only reliable way to tell them apart is to diff line
 * quantities against the cart as it was before the change.
 * @param {Object} cart - The updated cart
 */
function reportCartChange(cart) {
  if (!cart || !Array.isArray(cart.items)) return;

  const next = snapshot(cart);
  const previous = state.cart;
  const added = [];
  const removed = [];

  for (const [key, entry] of Object.entries(next)) {
    const delta = entry.quantity - (previous[key]?.quantity ?? 0);
    if (delta > 0) added.push({ ...entry.item, quantity: delta });
  }

  for (const [key, entry] of Object.entries(previous)) {
    const delta = entry.quantity - (next[key]?.quantity ?? 0);
    if (delta > 0) removed.push({ ...entry.item, quantity: delta });
  }

  pushEcommerce('add_to_cart', added);
  pushEcommerce('remove_from_cart', removed);

  state.cart = next;
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  let cart = event.detail?.resource;

  // Some dispatch sites pass an empty object when the cart request failed, so
  // fall back to reading the cart directly rather than reporting nothing.
  if (!cart || !Array.isArray(cart.items)) {
    try {
      cart = await (await fetch(`${cartUrl}.js`)).json();
    } catch {
      return;
    }
  }

  reportCartChange(cart);
});

/**
 * Resolves a clicked card to the item recorded for the list it was rendered in.
 * datalayer-view-item-list tags each card with data-dl-list, so pages holding
 * more than one list attribute the click correctly. Untagged cards fall back to
 * any list that knows the product, which covers cards injected after render.
 * @param {Element} element - The clicked card
 */
function findListItem(element) {
  const productId = element.dataset.productId;
  const listKey = element.dataset.dlList;

  if (listKey) return state.lists[listKey]?.[productId];

  for (const list of Object.values(state.lists)) {
    if (list[productId]) return list[productId];
  }

  return undefined;
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // select_item — reuses the item recorded by datalayer-view-item-list so the
  // reported price and position match what the shopper actually saw.
  const card = target.closest('[data-product-id]');
  if (card && target.closest('a[href]')) {
    const item = findListItem(card);

    if (item) {
      const { product_id, ...reported } = item;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: item.item_list_id,
          item_list_name: item.item_list_name,
          items: [reported],
        },
      });
    }
  }

  // component_click — opt in by adding data-dl-component="name" to any element,
  // so CRO tracking can be added without touching this file.
  const component = target.closest('[data-dl-component]');
  if (component) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'component_click',
      component_name: component.dataset.dlComponent,
      component_text: component.textContent?.trim().slice(0, 100) || undefined,
      page_type: (typeof Theme !== 'undefined' && Theme.template?.name) || undefined,
    });
  }
});
